/**
 * The API client.
 *
 * Two rules the whole app inherits from here, both from blueprint 03 §3.2:
 *
 * 1. **no request ever names a tenant.** The server resolves it from the
 *    session, so there is no tenant parameter on any function in this file and
 *    no place for a screen to invent one;
 * 2. **the environment filter travels as a query parameter and defaults to
 *    production by omission.** Sending nothing means production; sending
 *    something is an explicit, visible choice.
 */

export type Environment = 'production' | 'staging' | 'test' | 'development'

export interface ApiErrorBody {
  error: { code: string; message: string; details: Record<string, unknown> }
  correlation_id?: string
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly correlationId?: string,
    /** The envelope's `error.details` (09 §9.8): a 403's `reason`, a 422's field. Empty when the API sent none. */
    readonly details: Readonly<Record<string, unknown>> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * "This browser is not signed in." The only status the app reacts to on its
 * own: everything else is a failure to show, this one is a state to render
 * (blueprint 03 §3.5 — the session lives in a cookie the browser never reads,
 * so a 401 is the only way the app learns it has none).
 */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401
}

const BASE = '/api/v1'

function withEnvironments(path: string, environments?: readonly Environment[]): string {
  if (!environments || environments.length === 0) return path
  const query = environments.map((e) => `environment=${encodeURIComponent(e)}`).join('&')
  return `${path}${path.includes('?') ? '&' : '?'}${query}`
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!response.ok) {
    let body: ApiErrorBody | undefined
    try {
      body = (await response.json()) as ApiErrorBody
    } catch {
      body = undefined
    }
    throw new ApiError(
      response.status,
      body?.error.code ?? 'temporary_failure',
      body?.error.message ?? response.statusText,
      body?.correlation_id,
      body?.error.details ?? {},
    )
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export interface TenantSettings {
  display_name: string | null
  timezone: string
  currency: string
  locale: string
  support_email: string | null
}

export interface Website {
  id: string
  name: string
  environment: Environment
  primary_origin: string
  origin_host: string
  status: 'active' | 'suspended'
  external_site_reference: string | null
  created_at: string
}

export interface Tenant {
  id: string
  name: string
  slug: string
  status: 'active' | 'suspended'
  primary_host: string | null
  created_at: string
  settings: TenantSettings
  websites: Website[]
}

export interface EnvironmentOption {
  value: Environment
  label: string
  is_production: boolean
  website_count: number
}

export interface EnvironmentSelector {
  default: Environment[]
  active: Environment[]
  non_production_active: boolean
  options: EnvironmentOption[]
}

export interface FeatureFlag {
  key: string
  description: string
  enabled: boolean
  default: boolean
  overridden: boolean
}

export interface FeatureFlags {
  flags: FeatureFlag[]
  navigation: string[]
}

export interface DailyMetricRow {
  website_id: string
  environment: Environment
  metric_date: string
  sessions: number
  events: number
  orders: number
  gross_revenue: number
  currency: string
}

export interface DailyMetrics {
  environments: Environment[]
  non_production: boolean
  start: string
  end: string
  rows: DailyMetricRow[]
}

/** Mirrors `modules.auth.schemas.RoleOut`: the role's value and its label. */
export interface RoleRef {
  value: string
  label: string
}

export interface Membership {
  tenant_id: string
  role: RoleRef
  is_current: boolean
}

/**
 * Mirrors `modules.auth.schemas.SessionOut`. Who the caller is and where they
 * may act — never a token, a session id or a provider claim.
 */
export interface AuthSession {
  user_id: string
  email: string
  display_name: string | null
  tenant_id: string
  role: RoleRef
  mfa: boolean
  memberships: Membership[]
  requires_tenant_choice: boolean
}

/** Mirrors `modules.auth.schemas.SignOutOut`. */
export interface SignOut {
  provider_logout_url: string | null
}

export interface MyPermissions {
  role: string | null
  permissions: string[]
}

export const api = {
  currentTenant: () => request<Tenant>('/tenancy/me'),
  websites: (environments?: readonly Environment[]) =>
    request<Website[]>(withEnvironments('/tenancy/websites', environments)),
  environmentSelector: (environments?: readonly Environment[]) =>
    request<EnvironmentSelector>(withEnvironments('/tenancy/environments', environments)),
  featureFlags: () => request<FeatureFlags>('/tenancy/feature-flags'),
  setFeatureFlag: (key: string, enabled: boolean) =>
    request<FeatureFlags>(`/tenancy/feature-flags/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),
  dailyMetrics: (environments?: readonly Environment[]) =>
    request<DailyMetrics>(withEnvironments('/analytics/daily-metrics', environments)),
  myPermissions: () => request<MyPermissions>('/rbac/me'),
  /** The caller's own session: their name, email and role for the user menu. */
  authSession: () => request<AuthSession>('/auth/session'),
  /** Ends this session server-side and clears its cookie. */
  logout: () => request<SignOut>('/auth/logout', { method: 'POST' }),
}

export { withEnvironments }
